import React from 'react';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';
import { Theme } from '@app/theme';
import { Field } from 'formik';
import { I18n, getLocale } from 'i18n';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Input, PasswordInput, passwordSchema } from '@app/form';
import { YupValidators } from 'lib/validators';

const useStyles = createUseStyles((theme: Theme) => ({
  sameLine: {
    display: 'flex',
    alignItems: 'center',
    marginRight: -theme.marginBase * 2,
  },
  input: {
    marginRight: theme.marginBase * 2,
  },
}));

export const accountSchemaFields = {
  firstName: YupValidators.isValidName().required(),
  lastName: YupValidators.isValidName().required(),
  email: Yup.string().email().required(),
  password: passwordSchema,
  passwordRepeat: Yup.string()
    .required()
    .oneOf([Yup.ref('password')], I18n.t('form.errors.passwordNotMatch')),
};

export const accountSchema = Yup.object().shape(accountSchemaFields);

export const accountInitialValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordRepeat: '',
  locale: getLocale(),
  timezone: moment.tz.guess(),
};

interface Props {
  namePrefix?: string;
  unchangeableFields?: {
    email?: boolean;
    firstName?: boolean;
    lastName?: boolean;
  };
}

export const AccountFields = ({ namePrefix, unchangeableFields }: Props) => {
  const classes = useStyles();
  const prefix = namePrefix ? `${namePrefix}.` : '';
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <div className={classes.sameLine}>
        <Input
          name={`${prefix}firstName`}
          label={t('form.account.firstName')}
          placeholder="Ex: Albert"
          containerClass={classes.input}
          noMargin
          autoComplete="given-name"
          disabled={unchangeableFields && unchangeableFields.firstName}
          required
        />
        <Input
          name={`${prefix}lastName`}
          label={t('form.account.lastName')}
          placeholder="Ex: Einstein"
          containerClass={classes.input}
          noMargin
          autoComplete="family-name"
          disabled={unchangeableFields && unchangeableFields.lastName}
          required
        />
      </div>
      <Input
        name={`${prefix}email`}
        label={t('form.account.email')}
        placeholder="Ex: albert.einstein@gmail.com"
        type="email"
        disabled={unchangeableFields && unchangeableFields.email}
        required={!unchangeableFields || !unchangeableFields.email}
      />
      <PasswordInput
        name={`${prefix}password`}
        label={t('form.account.password')}
        required
      />
      <Input
        name={`${prefix}passwordRepeat`}
        label={t('form.account.passwordRepeat')}
        placeholder="****"
        type="password"
        required
      />
      <Field name={`${prefix}locale`} type="hidden" />
      <Field name={`${prefix}timezone`} type="hidden" />
    </React.Fragment>
  );
};
